import { useState, useMemo, useEffect } from 'react'
import {
  Table,
  Select,
  Input,
  Button,
  Space,
  Typography,
  Modal,
  Alert,
  Checkbox,
  Empty,
} from 'antd'
import { SearchOutlined, ReloadOutlined, LinkOutlined, CalendarOutlined } from '@ant-design/icons'
import type { TableColumnsType, TablePaginationConfig } from 'antd'
import { useOtdFilials, useUpdateFilialBinding, useUpdateBinding, useBulkUpdateFilials, usePeriods } from '@/hooks/useRefOtdFilials'
import { useFilials, useServices } from '@/hooks/useRefs'
import type { RefOtdFilial } from '@/types/refOtdFilials'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'

const { Text } = Typography

export default function FilialBinding() {
  // Period selection (required)
  const [selectedPeriod, setSelectedPeriod] = useState<string | undefined>(undefined)

  // Фильтры
  const [searchValue, setSearchValue] = useState<string>('')
  const [filialFilter, setFilialFilter] = useState<number | undefined>(undefined)
  const [statusFilter, setStatusFilter] = useState<string | undefined>('Активно')
  const [onlyWithoutFilial, setOnlyWithoutFilial] = useState(false)
  const [servFilter, setServFilter] = useState<number | undefined>(undefined)
  const [surgicalFilter, setSurgicalFilter] = useState<boolean | undefined>(undefined)

  // Fetch periods
  const { data: periodsData, isLoading: periodsLoading } = usePeriods()
  const periods = periodsData?.periods || []

  // Auto-select first period if available
  useEffect(() => {
    if (periods.length > 0 && !selectedPeriod) {
      setSelectedPeriod(periods[0].period)
    }
  }, [periods, selectedPeriod])

  // Пагинация
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  // Массовые действия
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkFilialModalOpen, setBulkFilialModalOpen] = useState(false)
  const [bulkFilialCode, setBulkFilialCode] = useState<number | undefined>(undefined)

  // Debounced search filter для оптимизации
  const debouncedSearch = useDebouncedValue(searchValue, 500)

  // Загрузка данных
  const { data: filialsData, isLoading: filialsLoading } = useFilials()
  const filials = filialsData || []

  const { data: servicesData } = useServices()
  const services = servicesData || []

  const filters = useMemo(
    () => ({
      period: selectedPeriod,
      fil_code: onlyWithoutFilial ? null : filialFilter,
      status: statusFilter,
      page,
      page_size: pageSize,
      without_filial: onlyWithoutFilial || undefined,
      serv_code: onlyWithoutFilial ? null : servFilter,
      is_surgical: onlyWithoutFilial ? null : surgicalFilter ?? null,
      search: debouncedSearch || undefined,
    }),
    [selectedPeriod, debouncedSearch, filialFilter, statusFilter, page, pageSize, onlyWithoutFilial, servFilter, surgicalFilter]
  )

  const { data, isLoading } = useOtdFilials(filters)
  const updateMutation = useUpdateFilialBinding()
  const updateBindingMutation = useUpdateBinding()
  const bulkUpdateMutation = useBulkUpdateFilials()

  // Обработчики
  const handleFilialChange = async (id_otd: string, fil_code: number | null) => {
    if (!selectedPeriod) return
    await updateMutation.mutateAsync({ id_otd, period: selectedPeriod, fil_code })
  }

  const handleServiceChange = async (id_otd: string, serv_code: number | null) => {
    if (!selectedPeriod) return
    await updateBindingMutation.mutateAsync({ id_otd, period: selectedPeriod, data: { serv_code } })
  }

  const handleSurgicalChange = async (id_otd: string, is_surgical: boolean | null) => {
    if (!selectedPeriod) return
    await updateBindingMutation.mutateAsync({ id_otd, period: selectedPeriod, data: { is_surgical } })
  }

  const handleResetFilters = () => {
    setSearchValue('')
    setFilialFilter(undefined)
    setStatusFilter('Активно')
    setOnlyWithoutFilial(false)
    setServFilter(undefined)
    setSurgicalFilter(undefined)
    setPage(1)
    // Don't reset period - keep it selected
  }

  const handleBulkUpdate = async () => {
    if (!bulkFilialCode || !selectedPeriod) {
      return
    }

    const updates = selectedRowKeys.map((id_otd) => ({
      id_otd: String(id_otd),
      fil_code: bulkFilialCode,
    }))

    await bulkUpdateMutation.mutateAsync({ period: selectedPeriod, updates })
    setSelectedRowKeys([])
    setBulkFilialModalOpen(false)
    setBulkFilialCode(undefined)
  }

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPage(pagination.current || 1)
    setPageSize(pagination.pageSize || 50)
  }

  // Статистика
  const stats = useMemo(() => {
    if (!data) return { total: 0, withoutFilial: 0, withoutFilialActive: 0, withoutFilialClosed: 0, withoutService: 0, withoutProfileType: 0 }

    return {
      total: data.total,
      withoutFilial: data.without_filial_count,
      withoutFilialActive: data.without_filial_active,
      withoutFilialClosed: data.without_filial_closed,
      withoutService: data.without_service_count,
      withoutProfileType: data.without_profile_type_count,
    }
  }, [data])

  // Колонки таблицы
  const columns: TableColumnsType<RefOtdFilial> = [
    {
      title: 'ID',
      dataIndex: 'id_otd',
      key: 'id_otd',
      width: 80,
      fixed: 'left',
      sorter: (a, b) => (a.id_otd || '').localeCompare(b.id_otd || ''),
    },
    {
      title: 'Подразделение',
      dataIndex: 'podr_name_short',
      key: 'podr_name_short',
      width: 180,
      ellipsis: true,
      sorter: (a, b) => (a.podr_name_short || '').localeCompare(b.podr_name_short || ''),
    },
    {
      title: 'Отделение',
      dataIndex: 'code_otd_name_full',
      key: 'code_otd_name_full',
      ellipsis: true,
      width: 280,
      sorter: (a, b) => (a.code_otd_name_full || '').localeCompare(b.code_otd_name_full || ''),
    },
    {
      title: 'Адрес',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
      width: 280,
      sorter: (a, b) => (a.address || '').localeCompare(b.address || ''),
    },
    {
      title: 'Филиал',
      dataIndex: 'fil_code',
      key: 'fil_code',
      width: 200,
      render: (fil_code: number | null, record: RefOtdFilial) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Выберите филиал"
          value={fil_code || undefined}
          onChange={(value) => handleFilialChange(record.id_otd, value || null)}
          loading={updateMutation.isPending}
          showSearch
          allowClear
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={filials.map((f) => ({
            value: f.fil_code,
            label: `${f.fil_code} - ${f.fil_name}`,
          }))}
        />
      ),
    },
    {
      title: 'Служба',
      dataIndex: 'serv_code',
      key: 'serv_code',
      width: 160,
      render: (serv_code: number | null, record: RefOtdFilial) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Выберите службу"
          value={serv_code || undefined}
          onChange={(value) => handleServiceChange(record.id_otd, value || null)}
          loading={updateBindingMutation.isPending}
          showSearch
          allowClear
          optionFilterProp="label"
          options={services.map((s) => ({
            value: s.serv_code,
            label: s.serv_name,
          }))}
        />
      ),
    },
    {
      title: 'Хир.',
      dataIndex: 'is_surgical',
      key: 'is_surgical',
      width: 70,
      align: 'center',
      render: (is_surgical: boolean | null, record: RefOtdFilial) => (
        <Checkbox
          checked={is_surgical ?? false}
          onChange={(e) => handleSurgicalChange(record.id_otd, e.target.checked)}
        />
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      render: (status: string) => {
        const isActive = status === 'Активно' || status === '1' || status === 'True'
        return (
          <Text type={isActive ? 'success' : 'secondary'}>
            {isActive ? 'Активно' : 'Закрыто'}
          </Text>
        )
      },
    },
  ]

  // Period options for select
  const periodOptions = periods.map((p) => ({
    value: p.period,
    label: `${p.period_label} (${p.count} записей)`,
  }))

  // If no periods exist, show message
  if (!periodsLoading && periods.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Space direction="vertical">
            <Text>Нет загруженных данных</Text>
            <Text type="secondary">
              Сначала загрузите структуру на вкладке "Загрузка структуры"
            </Text>
          </Space>
        }
      />
    )
  }

  return (
    <div>
      {/* Выбор периода */}
      <Space style={{ marginBottom: 16 }}>
        <CalendarOutlined />
        <Text strong>Период:</Text>
        <Select
          style={{ width: 250 }}
          placeholder="Выберите период"
          value={selectedPeriod}
          onChange={(value) => {
            setSelectedPeriod(value)
            setPage(1)
          }}
          loading={periodsLoading}
          options={periodOptions}
        />
      </Space>

      {/* Статистика */}
      <Alert
        message={
          <Space>
            <Text>Всего отделений: {stats.total}</Text>
            <Text type="warning">
              Без филиала: {stats.withoutFilial} ({stats.withoutFilialActive} — Активно, {stats.withoutFilialClosed} — Закрыто)
            </Text>
            <Text type="warning">
              Без службы: {stats.withoutService}
            </Text>
            <Text type="warning">
              Без типа профиля: {stats.withoutProfileType}
            </Text>
          </Space>
        }
        type="info"
        style={{ marginBottom: 16 }}
      />

      {/* Фильтры */}
      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="Поиск по отделению и адресу"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value)
              setPage(1)
            }}
            prefix={<SearchOutlined />}
            style={{ width: 300 }}
            allowClear
          />

          <Select
            placeholder="Филиал"
            value={filialFilter}
            onChange={(value) => {
              setFilialFilter(value)
              setOnlyWithoutFilial(false)
              setPage(1)
            }}
            style={{ width: 250 }}
            allowClear
            showSearch
            loading={filialsLoading}
            disabled={onlyWithoutFilial}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={filials.map((f) => ({
              value: f.fil_code,
              label: `${f.fil_code} - ${f.fil_name}`,
            }))}
          />

          <Checkbox
            checked={onlyWithoutFilial}
            onChange={(e) => {
              setOnlyWithoutFilial(e.target.checked)
              if (e.target.checked) {
                setFilialFilter(undefined)
              }
              setPage(1)
            }}
          >
            Только без филиала
          </Checkbox>

          <Select
            placeholder="Служба"
            value={servFilter}
            onChange={(value) => {
              setServFilter(value)
              setPage(1)
            }}
            style={{ width: 200 }}
            allowClear
            showSearch
            optionFilterProp="label"
            options={services.map((s) => ({
              value: s.serv_code,
              label: s.serv_name,
            }))}
          />

          <Select
            placeholder="Хир. активность"
            value={surgicalFilter}
            onChange={(value) => {
              setSurgicalFilter(value)
              setPage(1)
            }}
            style={{ width: 180 }}
            allowClear
            options={[
              { value: true, label: 'Хирургический' },
              { value: false, label: 'Не хирургический' },
            ]}
          />

          <Select
            placeholder="Статус"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value)
              setPage(1)
            }}
            style={{ width: 150 }}
            allowClear
            options={[
              { value: 'Активно', label: 'Активно' },
              { value: 'Закрыто', label: 'Закрыто' },
            ]}
          />

          <Button icon={<ReloadOutlined />} onClick={handleResetFilters}>
            Сбросить фильтры
          </Button>
        </Space>

        {/* Массовые действия */}
        {selectedRowKeys.length > 0 && (
          <Alert
            message={
              <Space>
                <Text>Выбрано отделений: {selectedRowKeys.length}</Text>
                <Button
                  type="primary"
                  icon={<LinkOutlined />}
                  onClick={() => setBulkFilialModalOpen(true)}
                >
                  Присвоить филиал выбранным
                </Button>
                <Button onClick={() => setSelectedRowKeys([])}>Отменить выбор</Button>
              </Space>
            }
            type="success"
          />
        )}
      </Space>

      {/* Таблица */}
      <Table
        columns={columns}
        dataSource={data?.items || []}
        rowKey="id_otd"
        loading={isLoading}
        scroll={{ x: 1500 }}
        size="small"
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data?.total || 0,
          showSizeChanger: true,
          showTotal: (total) => `Всего ${total} записей`,
          pageSizeOptions: ['50', '100', '200', '500'],
        }}
        onChange={handleTableChange}
      />

      {/* Modal для массового присвоения филиала */}
      <Modal
        title="Присвоить филиал выбранным отделениям"
        open={bulkFilialModalOpen}
        onOk={handleBulkUpdate}
        onCancel={() => {
          setBulkFilialModalOpen(false)
          setBulkFilialCode(undefined)
        }}
        okText="Присвоить"
        cancelText="Отмена"
        okButtonProps={{ disabled: !bulkFilialCode }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Выбрано отделений: {selectedRowKeys.length}</Text>
          <Select
            placeholder="Выберите филиал"
            value={bulkFilialCode}
            onChange={setBulkFilialCode}
            style={{ width: '100%' }}
            showSearch
            loading={filialsLoading}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={filials.map((f) => ({
              value: f.fil_code,
              label: `${f.fil_code} - ${f.fil_name}`,
            }))}
          />
        </Space>
      </Modal>
    </div>
  )
}
